/* global HTMLCanvasElement, HTMLImageElement, document */

const MIN_QUALITY = 0.3;
const QUALITY_STEP = 0.1;
const INITIAL_QUALITY = 0.85;

/**
 * Resizes a base64 image if it exceeds the specified maximum size.
 * Uses a canvas element to re-encode the image. Iteratively reduces
 * JPEG quality until the output is under the threshold or minimum quality is reached.
 */
export async function resizeImageIfNeeded(
  base64: string,
  mimeType: string,
  maxSizeKB: number
): Promise<{ base64: string; mimeType: string; width: number; height: number }> {
  // Check current size (base64 is ~4/3 of binary size)
  const estimatedSizeKB = (base64.length * 3) / 4 / 1024;
  if (estimatedSizeKB <= maxSizeKB) {
    const img = await loadImage(base64, mimeType);
    return { base64, mimeType, width: img.width, height: img.height };
  }

  // Load the image
  const img = await loadImage(base64, mimeType);

  // Calculate scale factor to reduce dimensions proportionally
  const scaleFactor = Math.sqrt(maxSizeKB / estimatedSizeKB);
  const newWidth = Math.max(1, Math.round(img.width * scaleFactor));
  const newHeight = Math.max(1, Math.round(img.height * scaleFactor));

  // Draw onto a canvas at reduced dimensions
  const canvas = document.createElement("canvas") as HTMLCanvasElement;
  canvas.width = newWidth;
  canvas.height = newHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, newWidth, newHeight);

  // Iteratively reduce JPEG quality until under the threshold
  let quality = INITIAL_QUALITY;
  let resizedBase64: string;

  do {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    resizedBase64 = dataUrl.split(",")[1];

    const currentSizeKB = (resizedBase64.length * 3) / 4 / 1024;
    if (currentSizeKB <= maxSizeKB) {
      break;
    }

    quality -= QUALITY_STEP;
  } while (quality >= MIN_QUALITY);

  return { base64: resizedBase64, mimeType: "image/jpeg", width: newWidth, height: newHeight };
}

function loadImage(base64: string, mimeType: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = `data:${mimeType};base64,${base64}`;
  });
}
